import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Nomination, AwardType, NominationStatus, UpdateNominationInput } from '../../types/nomination';
import { NominationServiceWrapper } from '../../services/nominationServiceWrapper';
import { useAuth } from '../../contexts/AuthContext';
import { getStatusBadgeClass, formatNominationDate } from './NominationCard';
import PageHeader from '../common/PageHeader';
import './NominationAdmin.css';

const NominationAdmin: React.FC = () => {
  const renderStart = performance.now();
  console.log('[Perf][ManageNominations] render start');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [nominations, setNominations] = useState<Nomination[]>([]);
  const [editTarget, setEditTarget] = useState<Nomination | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Nomination | null>(null);
  const [editForm, setEditForm] = useState<UpdateNominationInput>({});
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  const loadNominations = useCallback(async () => {
    const startedAt = performance.now();
    console.log('[Perf][ManageNominations] API start NominationServiceWrapper.getAllNominations');
    const all = await NominationServiceWrapper.getAllNominations();
    console.log('[Perf][ManageNominations] API complete NominationServiceWrapper.getAllNominations', {
      durationMs: Math.round(performance.now() - startedAt),
      count: all.length,
    });
    setNominations(all);
  }, []);

  useEffect(() => { loadNominations(); }, [loadNominations]);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleEditClick = (nom: Nomination) => {
    setEditTarget(nom);
    setEditForm({ nomineeName: nom.nomineeName, awardType: nom.awardType, reason: nom.reason, supportingDetails: nom.supportingDetails || '' });
  };

  const handleEditSave = async () => {
    if (!editTarget) return;
    const result = await NominationServiceWrapper.updateNomination(editTarget.id, editForm, user?.email || 'unknown', user?.name || 'Unknown');
    if (result.success) { showToast(`${editTarget.nomineeName} updated`, 'success'); setEditTarget(null); loadNominations(); }
    else showToast(result.errors[0] || 'Failed to update', 'error');
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const result = await NominationServiceWrapper.deleteNomination(deleteTarget.id, user?.email || 'unknown', user?.name || 'Unknown');
    if (result.success) { showToast(`${deleteTarget.nomineeName} deleted`, 'success'); setDeleteTarget(null); loadNominations(); }
    else showToast(result.errors[0] || 'Failed to delete', 'error');
  };

  const filtered = nominations
    .filter(n => filter === 'all' || n.status === filter)
    .filter(n => !search || n.nomineeName.toLowerCase().includes(search.toLowerCase()) || n.nominatorName.toLowerCase().includes(search.toLowerCase()));

  const statusCounts: Record<string, number> = { all: nominations.length };
  Object.values(NominationStatus).forEach(s => { statusCounts[s] = nominations.filter(n => n.status === s).length; });

  useEffect(() => {
    console.log('[Perf][ManageNominations] render complete', {
      durationMs: Math.round(performance.now() - renderStart),
      nominations: nominations.length,
      filtered: filtered.length,
      filter,
      search,
    });
  });

  return (
    <div className="na-page">
      <PageHeader
        icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>}
        title="Manage Nominations"
        subtitle={`${nominations.length} total nominations`}
        gradient="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 50%, #6d28d9 100%)"
        accentColor="#8b5cf6"
      />

      <div className="na-toolbar">
        <input type="text" className="na-search" placeholder="Search by name..." value={search} onChange={e => setSearch(e.target.value)} />
        <div className="na-filters">
          {[['all', 'All'], ...Object.values(NominationStatus).map(s => [s, s])].map(([key, label]) => (
            <button key={key} className={`nom-filter-btn ${filter === key ? 'active' : ''}`} onClick={() => setFilter(key)}>
              {label} {statusCounts[key] > 0 && <span className="nom-filter-count">{statusCounts[key]}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="na-items">
        {filtered.length === 0 ? (
          <div className="na-empty">
            <h3>No Nominations Found</h3>
            <p>Try adjusting your filters or search.</p>
          </div>
        ) : (
          <div className="na-table">
            <div className="na-table-header">
              <span className="na-col-nominee">Nominee/Team</span>
              <span className="na-col-award">Award</span>
              <span className="na-col-nominator">Nominated By</span>
              <span className="na-col-status">Status</span>
              <span className="na-col-date">Date</span>
              <span className="na-col-actions">Actions</span>
            </div>
            {filtered.map(nom => (
              <div className="na-table-row" key={nom.id}>
                <div className="na-col-nominee">
                  <div className="na-row-nominee" onClick={() => navigate(`/nominations/${nom.id}`)}>
                    {nom.nomineePhotoUrl ? (
                      <img src={nom.nomineePhotoUrl} alt={nom.nomineeName} className="na-row-photo" />
                    ) : (
                      <div className="na-row-avatar">{nom.nomineeName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}</div>
                    )}
                    <span className="na-row-name">{nom.nomineeName}</span>
                  </div>
                </div>
                <span className="na-col-award na-row-award">{nom.awardType}</span>
                <span className="na-col-nominator na-row-text">{nom.nominatorName}</span>
                <span className="na-col-status"><span className={`nomination-status-badge ${getStatusBadgeClass(nom.status)}`}>{nom.status}</span></span>
                <span className="na-col-date na-row-date">{formatNominationDate(nom.createdAt)}</span>
                <div className="na-col-actions na-row-actions">
                  <button className="na-action-btn na-btn-edit" onClick={() => handleEditClick(nom)} title="Edit">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </button>
                  <button className="na-action-btn na-btn-delete" onClick={() => setDeleteTarget(nom)} title="Delete">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editTarget && (
        <div className="na-overlay" onClick={() => setEditTarget(null)}>
          <div className="na-modal" onClick={e => e.stopPropagation()}>
            <h3>Edit Nomination</h3>
            <label className="na-modal-label">Nominee/Team Name</label>
            <input type="text" className="na-modal-input" value={editForm.nomineeName || ''} onChange={e => setEditForm(prev => ({ ...prev, nomineeName: e.target.value }))} />
            <label className="na-modal-label">Award Type</label>
            <select className="na-modal-input" value={editForm.awardType || ''} onChange={e => setEditForm(prev => ({ ...prev, awardType: e.target.value as AwardType }))}>
              {Object.values(AwardType).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <label className="na-modal-label">Reason</label>
            <textarea className="na-modal-textarea" value={editForm.reason || ''} onChange={e => setEditForm(prev => ({ ...prev, reason: e.target.value }))} />
            <label className="na-modal-label">Supporting Details</label>
            <textarea className="na-modal-textarea" value={editForm.supportingDetails || ''} onChange={e => setEditForm(prev => ({ ...prev, supportingDetails: e.target.value }))} />
            <div className="na-modal-actions">
              <button className="rq-action-btn rq-btn-note" onClick={() => setEditTarget(null)}>Cancel</button>
              <button className="rq-action-btn rq-btn-approve" onClick={handleEditSave}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteTarget && (
        <div className="na-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="na-modal" onClick={e => e.stopPropagation()}>
            <h3>Delete Nomination</h3>
            <p className="na-delete-warning">Are you sure you want to delete the nomination for <strong>{deleteTarget.nomineeName}</strong>? This action is recorded in the audit log and cannot be undone.</p>
            <div className="na-modal-actions">
              <button className="rq-action-btn rq-btn-note" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="rq-action-btn rq-btn-reject" onClick={handleDeleteConfirm}>Confirm Delete</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className={`rq-toast ${toast.type}`} role="alert">{toast.message}</div>}
    </div>
  );
};

export default NominationAdmin;
